import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { reduce } from "lodash";

import type { IMainSearchTransactionSuggestion, MainSearchSource, MainSearchSuggestionsScope } from "@/shop/api/mainSearch";
import {
  mainSearchFeatureFlags,
  mainSearchIndustryRestriction,
  mainSearchRequiresLogin,
  mainSearchSources,
  mainSearchSuggestionsFeatureFlags,
  mainSearchSuggestionsIndustryRestriction,
  mainSearchSuggestionsRequiresLogin,
  mainSearchSuggestionsScopes
} from "@/shop/api/mainSearch";
import { deriveSearchTableRows } from "@/shop/reducers/partTreeSearch/selectors"; // TODO: move to common
import { getPersonalizedFeatures } from "@/shop/reducers/personalized";
import { getIndustry, getIsLoggedIn } from "@/shop/reducers/session";
import type { IShopRootState } from "@/shop/reducers/types";
import type { IPager } from "@/shop/types/common";
import type { IMainSearchFilterDefinition, IMainSearchFilterSetting } from "@/shop/types/mainSearch";
import type { IPagingQuery } from "@/shop/types/searchTable";

import { DEFAULT_PAGE_SIZE_MAIN_SEARCH_RESULTS, PAGE_SIZE_OPTIONS } from "./constants"; // TODO: move to common
import type { IMainSearchQueryState, IMainSearchResultState, IMainSearchState, IMainSearchSuggestionsResultState } from "./types";

// state slices
export const getMainSearchState: Selector<IShopRootState, IMainSearchState> = (state) => state.mainSearch;
export const getMainSearchQueryState: Selector<IShopRootState, IMainSearchQueryState> = (state) => state.mainSearch.query;

export const getMainSearchResultsState: Selector<IShopRootState, { [id in MainSearchSource]: IMainSearchResultState }> = (state) => state.mainSearch.results;

export const getMainSearchSuggestionResultsState: Selector<IShopRootState, { [id in MainSearchSuggestionsScope]: IMainSearchSuggestionsResultState }> = (
  state
) => state.mainSearch.suggestionResults;

export const getMainSearchSuggestionIsFetching: Selector<IShopRootState, boolean> = (state) => state.mainSearch.query.isFetchingSearchSuggestions;

// pager

export const getSearchResultCount: (state: IShopRootState, props: { collection: MainSearchSource }) => number = (
  state: IShopRootState,
  props: { collection: MainSearchSource }
) => {
  const mainSearch = getMainSearchState(state);
  return mainSearch.results[props.collection] && mainSearch.results[props.collection].data && mainSearch.results[props.collection].data.count
    ? mainSearch.results[props.collection].data.count
    : 0;
};

export const getSearchResultCountSelector: (state: IShopRootState, props: { collection: MainSearchSource }) => number = createSelector(
  [getSearchResultCount],
  (state) => state
);

export const getPagingQuery: (state: IShopRootState, props: { collection: MainSearchSource }) => IPagingQuery = (
  state: IShopRootState,
  props: { collection: MainSearchSource }
) => {
  const mainSearch = getMainSearchState(state);
  return mainSearch.results[props.collection] && mainSearch.results[props.collection].query
    ? mainSearch.results[props.collection].query
    : { limit: DEFAULT_PAGE_SIZE_MAIN_SEARCH_RESULTS!, offset: 0 };
};

export const getPagerOffset: (state: IShopRootState, props: { collection: MainSearchSource }) => number = (
  state: IShopRootState,
  props: { collection: MainSearchSource }
) => {
  const mainSearch = getMainSearchState(state);
  return mainSearch.results[props.collection] && mainSearch.results[props.collection].query.offset ? mainSearch.results[props.collection].query.offset : 0;
};

export const getPagerOffsetSelector: (state: IShopRootState, props: { collection: MainSearchSource }) => number = createSelector(
  [getPagerOffset],
  (state) => state
);

export const getPagerLimit: (state: IShopRootState, props: { collection: MainSearchSource }) => number = (
  state: IShopRootState,
  props: { collection: MainSearchSource }
) => {
  const mainSearch = getMainSearchState(state);
  return mainSearch.results[props.collection] && mainSearch.results[props.collection].query.limit ? mainSearch.results[props.collection].query.limit : 0;
};

export const getPagerLimitSelector: (state: IShopRootState, props: { collection: MainSearchSource }) => number = createSelector(
  [getPagerLimit],
  (state) => state
);

export const getServerPager: (state: IShopRootState, props: { collection: MainSearchSource }) => { count: number; limit: number; offset: number } =
  createSelector([getSearchResultCountSelector, getPagerLimitSelector, getPagerOffsetSelector], (count, limit, offset) => ({
    count,
    limit,
    offset,
    sort: null! // TODO: currently not implemented, since is not a requirement right now
  }));

export const getSearchPager: (state: IShopRootState, props: { collection: MainSearchSource }) => IPager = createSelector([getServerPager], (pager) =>
  deriveClientPager(pager)
);

export function getSearchFilterDefinition(source: MainSearchSource): Selector<IShopRootState, IMainSearchFilterDefinition[]> {
  return (state) => getMainSearchState(state).filters[source]?.filterDefinitions ?? [];
}

export function getSearchFiltersSetting(source: MainSearchSource): Selector<IShopRootState, IMainSearchFilterSetting[]> {
  return (state) => getMainSearchState(state).filters[source]?.filterSetting ?? [];
}

export const getSearchHasFiltersSetting = createSelector(getMainSearchState, (state) =>
  reduce(
    state.filters,
    (memo, data, source) => {
      return {
        ...memo,
        [source]: Boolean(data.filterSetting?.length)
      };
    },
    {} as Record<MainSearchSource, boolean>
  )
);

export function deriveClientPager({ count, limit, offset }: { count: number; limit: number; offset: number }): IPager {
  const pageSize = limit;
  const currentPage = Math.floor(offset / limit) + 1;
  const numPages = Math.max(currentPage, Math.ceil(count / limit));
  const pageSizeOptions = PAGE_SIZE_OPTIONS;
  return {
    numPages,
    currentPage,
    pageSize,
    pageSizeOptions
  };
}

// get results

/**
 * Get result scopes for main search
 * - order determines the order of tabs in search
 * - determines which results are fetched
 *
 *  ⚠️ Note about checking feature flags when fetching
 * - improve search performance by reducing the number of requests
 * - might cause a race condition between flags and search when reloading the page
 * - the check here can be safely removed
 */
export const getMainSearchResultsScopes: Selector<IShopRootState, MainSearchSource[]> = createSelector(
  getPersonalizedFeatures,
  getIndustry,
  getIsLoggedIn,
  (features, industry, isLoggedIn) =>
    mainSearchSources
      .filter((source) => {
        const flag = mainSearchFeatureFlags[source];
        return flag ? Boolean(features[flag]) : true;
      })
      .filter((source) => {
        const industries = mainSearchIndustryRestriction[source];
        return industries ? Boolean(industries.includes(industry!)) : true;
      })
      .filter((source) => {
        const flag = mainSearchRequiresLogin[source];
        return flag ? isLoggedIn : true;
      })
);

export const getMainSearchResultsStates: Selector<IShopRootState, IMainSearchResultState[]> = createSelector(
  getMainSearchResultsState,
  getMainSearchResultsScopes,
  (state, scopes) => scopes.map((scope) => state[scope]).filter(Boolean)
);

export const getMainSearchResultStateByScope: (state: IShopRootState, props: { collection: MainSearchSource }) => IMainSearchResultState = (
  state: IShopRootState,
  props: { collection: MainSearchSource }
) => {
  const mainSearch = getMainSearchState(state);
  return mainSearch.results[props.collection] ? mainSearch.results[props.collection] : null!;
};

export const getMainSearchResultStateByScopeSelector: (state: IShopRootState, props: { collection: MainSearchSource }) => IMainSearchResultState =
  createSelector([getMainSearchResultStateByScope], (state) => state);

export const getMainSearchStateBySource: (state: IShopRootState, props: { collection: MainSearchSource }) => IMainSearchResultState = createSelector(
  [getMainSearchResultStateByScope],
  (state) =>
    state == null
      ? null!
      : {
          ...state,
          data:
            state.data == null
              ? null!
              : {
                  ...state.data,
                  // FIXME: hack!
                  data: deriveSearchTableRows(state.data)
                }
        }
);

// search suggestions

/**
 * Get result scopes for main search suggestions
 * - order determines the order of collapsible items in search menu
 * - determines which suggestions are fetched
 *
 *  ⚠️ Note about checking feature flags when fetching
 * - improves search performance by reducing the number of requests
 * - should not cause cause a race condition between flags and search
 * - the check here can be safely removed
 */
export const getMainSearchSuggestionsScopes: Selector<IShopRootState, MainSearchSuggestionsScope[]> = createSelector(
  getPersonalizedFeatures,
  getIndustry,
  getIsLoggedIn,
  (features, industry, isLoggedIn) =>
    mainSearchSuggestionsScopes
      .filter((source) => {
        const flag = mainSearchSuggestionsFeatureFlags[source];
        return flag ? Boolean(features[flag]) : true;
      })
      .filter((source) => {
        const industries = mainSearchSuggestionsIndustryRestriction[source];
        return industries ? Boolean(industries.includes(industry!)) : true;
      })
      .filter((source) => {
        const flag = mainSearchSuggestionsRequiresLogin[source];
        return flag ? isLoggedIn : true;
      })
);

export const getMainSearchSuggestionResultsStates: Selector<IShopRootState, IMainSearchSuggestionsResultState[]> = createSelector(
  getMainSearchSuggestionResultsState,
  getMainSearchSuggestionsScopes,
  (state, scopes) => scopes.map((scope) => state[scope]).filter(Boolean)
);

// get query strings

export const getMainSearchText: Selector<IShopRootState, string> = createSelector(getMainSearchQueryState, (state) => state.textValueMainSearch);

export const getMainSearchSuggestionText: Selector<IShopRootState, string> = createSelector(
  getMainSearchQueryState,
  (state) => state.textValueSuggestionSearch
);

// get result counts

export const getMainSearchResultsCount: Selector<IShopRootState, number> = createSelector(getMainSearchResultsStates, (states) => {
  let count = 0;
  states.map((s) => {
    if (s.data && s.data.count) {
      count += s.data.count;
    }
  });
  return count;
});

export const getMainSearchSuggestionResultsCount: Selector<IShopRootState, number> = createSelector(getMainSearchSuggestionResultsStates, (states) => {
  let count = 0;
  states.map((s) => {
    if (s.data && s.source) {
      count += (s.data as IMainSearchTransactionSuggestion).count;
    }
  });
  return count;
});
